import { useTranslation } from 'react-i18next';

import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import PageLayout from '@/components/Layout/PageLayout';
import BioscopeStatusBox from '@/components/molecules/BioscopeStatusBox';
import ContentBlock from '@/components/molecules/ContentBlock';
import { EmptyState } from '@/components/molecules/EmptyState';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useCage, useCageName } from '@/contexts/cage';
import type { DomainCageStatus } from '@/domain/cages';
import type { ImageFeedback } from '@/domain/imageFeedback';
import type { ImageState } from '@/hooks/image-state';
import { useCageOverview } from '@/routes/Client/Cage/components/cage-overview-context';
import BioscopeDepthCard from '@/routes/Client/Cage/pages/CageDashboard/CageBioscopeStatus/components/BioscopeDepthCard/BioscopeDepthCard';
import SceneLabels from '@/routes/Client/Cage/pages/CageDashboard/CageBioscopeStatus/components/SceneLabels';
import { createCageUrl } from '@/utils/urlGenerator';

import styles from './CageBioscopeInspectionPageContent.module.scss';

/**
 * Holds the image viewer's box while the first frame is still on its way.
 *
 * The measurements are the viewer's own: the toolbar is the control bar's padding plus
 * ImageToolbar's 44px row, the frame is the stage figure's 2:1 ratio inside the nav
 * buttons' 50px columns, and the strip copies the filmstrip mask, card and day scrubber.
 */
function BioscopeImageViewerSkeleton() {
    const { t } = useTranslation();

    return (
        <div className={styles.viewerSlot}>
            <p className={styles.viewerStatus} role="status">
                {t('atoms.spinner.loading')}
            </p>
            <div className={styles.viewerSkeleton} aria-hidden="true">
                <SkeletonBox
                    className={styles.viewerToolbar}
                    width="100%"
                    borderRadius="var(--radius-8)"
                />
                <div className={styles.viewerStage}>
                    <SkeletonBox
                        className={styles.viewerStageFrame}
                        width="100%"
                        borderRadius="var(--radius-8)"
                    />
                </div>
                <div className={styles.viewerFilmstrip}>
                    <div className={styles.viewerFilmstripMask} />
                    <div className={styles.viewerFilmstripTrack}>
                        <SkeletonBox
                            className={styles.viewerFilmstripCard}
                            borderRadius="var(--radius-8)"
                        />
                    </div>
                    <div className={styles.viewerScrubber}>
                        <div className={styles.viewerScrubberRail} />
                        <div className={styles.viewerScrubberLabels} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export interface CageBioscopeInspectionPageContentProps {
    clientId: number;
    locationId: number;
    cageId: number;
    source: string;
    bioscopeImageState: ImageState;
    healthType?: string;
    isOptoScaleAdmin?: boolean;
    onCommitFalseDetection: (feedbackDto: ImageFeedback) => void;
    cageStatus?: DomainCageStatus | null;
    cageStatusIsLoading?: boolean;
    /** Try again for the pen's bioscope status, set only once its request failed. */
    cageStatusError?: RetryProps | null;
}

export default function CageBioscopeInspectionPageContent(
    props: CageBioscopeInspectionPageContentProps
) {
    const {
        clientId,
        locationId,
        cageId,
        bioscopeImageState,
        source,
        isOptoScaleAdmin = false,
        onCommitFalseDetection = () => {},
        cageStatus,
        cageStatusIsLoading = false,
        cageStatusError,
    } = props;
    const { t } = useTranslation();
    const { client } = useCage();
    const cageName = useCageName();
    const { loading: isLoadingOverview, data: overview } = useCageOverview();
    const getHistoricalBioscopeInspectionLink = (bioscopeId: number) =>
        `${createCageUrl(clientId, locationId, cageId)}/bioscope/${bioscopeId}`;

    const isColor = source === 'color';

    const hasBioscopeInspectionFeatureFlag =
        client?.featureFlags?.includes('BIOSCOPE_INSPECTION') ?? false;
    const bioscopeInspectionLink = cageStatus?.currentBioscope?.id
        ? `${createCageUrl(clientId, locationId, cageId)}/bioscope/${cageStatus.currentBioscope.id}`
        : '';

    const hasImages = bioscopeImageState.images.length > 0;
    const showInitialLoader = bioscopeImageState.loading && !hasImages;
    const showEmpty = !bioscopeImageState.loading && !hasImages;

    return (
        <PageLayout
            title={cageName}
            subtitle={t('common.labels.bioscopeStatus')}
            className={styles.pageBoxLayout}>
            <div className={styles.column}>
                <ContentBlock>
                    {showInitialLoader && <BioscopeImageViewerSkeleton />}
                    {showEmpty && <EmptyState title={t('common.labels.atTheMomentWeDo')} />}
                    {hasImages && (
                        <ImageViewer
                            key={`bioscope-${cageId}`}
                            variant="bioscope"
                            isOptoScaleAdmin={isOptoScaleAdmin}
                            isColor={isColor}
                            onCommitImageFeedback={onCommitFalseDetection}
                            options={{ magnifierEnabled: false }}
                            images={bioscopeImageState.images}
                            daysWithImages={bioscopeImageState.dayswithimages}
                            onDayChange={bioscopeImageState.onAfterTimelineChangeHandler}
                            initialImageIndex={bioscopeImageState.initialImageIndex}
                            isFetching={bioscopeImageState.isFetching}
                        />
                    )}
                </ContentBlock>
                <BioscopeDepthCard cageId={cageId} />
            </div>
            <div className={styles.column}>
                <ContentBlock title={t('bioscopeStatus.sceneLabels.title')}>
                    <SceneLabels
                        sceneLabels={overview.bioscope.sceneLabels}
                        isLoadingOverview={isLoadingOverview}
                    />
                </ContentBlock>

                <ContentBlock title={t('cageDashboard.common.cageStatus')}>
                    {cageStatusError ? (
                        <SomethingHappened {...cageStatusError} />
                    ) : (
                        <BioscopeStatusBox
                            cageStatus={cageStatus}
                            measurementCount={overview.biomass.period.measCnt ?? 0}
                            isLoading={cageStatusIsLoading || isLoadingOverview}
                            isOptoScaleAdmin={isOptoScaleAdmin}
                            showBioscopeInspectionLink={hasBioscopeInspectionFeatureFlag}
                            bioscopeInspectionLink={bioscopeInspectionLink}
                            historicalBioscopeInspectionLink={getHistoricalBioscopeInspectionLink}
                        />
                    )}
                </ContentBlock>
            </div>
        </PageLayout>
    );
}
